import { Fragment } from 'react';
import { DataCard } from '@/reports/shared/DataCard/DataCard';
import {
  build as buildTag,
  DataCardTagTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { AutomobileSalvage } from '@/reports/types/vehicles';
import { Text, Table } from '@/theme/index';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import {
  capitalizeString,
  commaFormat,
  commaFormatCurrency,
  formatPhone,
} from '@/utils/index';

interface VehicleSalvage {
  salvage: AutomobileSalvage;
}

export function VehicleSalvageCard({ salvage }: VehicleSalvage) {
  function CardTitleText() {
    return (
      <Text
        variant="h3"
        fontWeight="normal"
        mb={0}
        data-cy="vehicle-salvages-card-title"
      >
        Salvage obtained
        {salvage.obtained_date?.full && (
          <>
            <Text variant="h3" fontWeight="normal" component="span">
              {' '}
              on
            </Text>
            <Text variant="h3" component="span">
              {' '}
              {formatDateFromReport(salvage.obtained_date.full)}
            </Text>
          </>
        )}
        {salvage.location && (
          <>
            <Text variant="h3" fontWeight="normal" component="span">
              {' '}
              in
            </Text>
            <Text variant="h3" component="span">
              {' '}
              {salvage.location}
            </Text>
          </>
        )}
      </Text>
    );
  }

  let mileage = '';
  if (salvage.odometer) {
    mileage = !isNaN(Number(salvage.odometer))
      ? commaFormat(Number(salvage.odometer))
      : salvage.odometer.toString();
  }

  let forExport = '';
  if (salvage.intended_for_export !== null) {
    forExport = salvage.intended_for_export ? 'Yes' : 'No';
  }

  const salvageDamageData = [
    ['Primary damage', capitalizeString(salvage.primary_damage)],
    ['Secondary damage', capitalizeString(salvage.secondary_damage)],
    [
      'Estimated damage',
      salvage.estimated_damage
        ? commaFormatCurrency(salvage.estimated_damage)
        : '',
    ],
  ].filter((value) => Boolean(value[1]));

  const salvageGeneralData = [
    ['Type', salvage.type ? capitalizeString(salvage.type) : ''],
    ['Odoemeter', mileage],
    ['Exterior color', capitalizeString(salvage.exterior_color) || ''],
    ['Sale document', salvage.sale_document || ''],
    [
      'Disposition text',
      salvage.disposition_text ? salvage.disposition_text : '',
    ],
    ['Intended for export', forExport],
  ].filter((value) => Boolean(value[1]));

  const salvageSource = salvage.source;
  const parsedLocation = salvageSource?.contact?.addresses[0]?.parsed;
  const cityState = [parsedLocation?.city, parsedLocation?.state]
    .filter((el) => Boolean(el))
    .join(', ');
  const zip = parsedLocation?.zip5 ? ` ${parsedLocation?.zip5}` : '';

  const salvageSourceData = [
    ['Name', salvageSource?.name || ''],
    ['Website', salvageSource?.website || ''],
    [
      'Phone',
      salvageSource?.contact?.phones[0]?.number
        ? formatPhone(salvageSource?.contact?.phones[0].number)
        : '',
    ],
    ['Email', salvageSource?.contact?.emails[0]?.address || ''],
    ['Source', salvageSource?.source || ''],
    ['Location', `${cityState}${zip}` || ''],
  ].filter((value) => Boolean(value[1]));

  const tag = () => {
    const tagMapping: DataCardTagTruthinessMapping[] = [
      {
        name: 'premium',
        truthiness: salvage?.premium || false,
      },
    ];

    return buildTag(tagMapping);
  };

  return (
    <DataCard tag={tag()} testId="vehicle-salvages-card" id={salvage.id || ''}>
      <CardTitleText />
      {salvage.premium && (
        <Text variant="caption" mt={0}>
          Source: NMVTIS
        </Text>
      )}
      {salvageDamageData.length > 0 && (
        <Table
          rows={salvageDamageData}
          data-cy="vehicle-salvages-card-damage"
          className="flipped"
        />
      )}
      {salvageGeneralData.length > 0 && (
        <>
          <Text variant="h4">Salvage information</Text>
          <Table
            rows={salvageGeneralData}
            data-cy="vehicle-salvages-card-general"
            className="flipped striped"
          />
        </>
      )}
      {salvageSourceData.length > 0 && (
        <>
          <Text variant="h4">Salvage source information</Text>
          <Table
            rows={salvageSourceData}
            data-cy="vehicle-salvages-card-source"
            className="striped flipped"
          />
        </>
      )}
    </DataCard>
  );
}
